import { Link } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import { Check, XIcon, Star } from 'lucide-react';

const PRICING = [
  {
    name: '9-Step Pro System',
    subtitle: 'Complete Manual Day Trading System',
    price: 497, originalPrice: 697,
    slug: '9-step-pro-system',
    accent: null,
    badge: 'Most Popular — Day Trading',
    features: [
      { text: "Full 9-Step day trading strategy — Hettie's proven framework", included: true },
      { text: 'Two in-depth, step-by-step training videos', included: true },
      { text: 'Three workbooks to test and reinforce your knowledge', included: true },
      { text: 'Built-in risk and money management rules', included: true },
      { text: 'Trading checklist to guide every step of your process', included: true },
      { text: 'Usure EA — on-chart assistant that keeps you disciplined so you never miss a step', included: true },
      { text: 'Top-down analysis: 1-Hour → 15-Minute → 5-Minute charts', included: true },
      { text: '1-Hour candle pattern recognition at key levels', included: true },
      { text: 'MACD + Stochastic + EMA cross confirmation filters', included: true },
      { text: 'Complete Day Trading Manual (PDF)', included: true },
      { text: 'Lifetime license with free updates', included: true },
    ],
    popular: true,
  },
  {
    name: 'Bow-Tie Scalping System',
    subtitle: 'Complete Scalping Strategy',
    price: 297, originalPrice: null,
    slug: 'bowtie-scalping-system',
    accent: 'scalp',
    badge: 'Scalping Strategy',
    features: [
      { text: 'Bowtie EA — H1 EMA cross scalping alerts', included: true },
      { text: 'Bowtie1 EA — M15 bias filter + M5 gap check', included: true },
      { text: "Learn the 'Bow-Tie' scalping signal pattern", included: true },
      { text: 'H1 direction + M15 bias + M5 entry confirmation', included: true },
      { text: 'Complete Bow-Tie Scalping Manual (PDF)', included: true },
      { text: 'Quick-fire entry/exit rules for scalping', included: true },
      { text: 'MT5 optimized for fast execution', included: true },
      { text: 'Lifetime license with free updates', included: true },
    ],
    popular: false,
  },
];

export default function IntermediatePage() {
  const { addItem, items } = useCart();

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="intermediate-page">
      {/* Hero */}
      <section className="px-4 sm:px-6 lg:px-8 pb-16 text-center relative overflow-hidden">
        <div className="absolute top-0 left-1/3 w-[500px] h-[500px] rounded-full bg-amber-400/[0.03] blur-[150px]" />
        <div className="relative mx-auto max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-400/80 mb-3">Level 2 — Build Your Edge</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight">
            Intermediate <span className="text-amber-400">Courses</span>
          </h1>
          <p className="mt-5 text-sm sm:text-base text-white/40 max-w-2xl mx-auto leading-relaxed">
            Step up from the foundations. The 9-Step Pro System gives you a complete, step-by-step day trading framework — the same one Hettie uses in her own daily trading.
          </p>
          <div className="mt-6 flex items-center justify-center gap-6 text-xs text-white/30">
            <span>Manual Trading System</span>
            <span className="w-1 h-1 rounded-full bg-white/20" />
            <span>Usure EA Included</span>
            <span className="w-1 h-1 rounded-full bg-white/20" />
            <span>30-Day Money Back</span>
          </div>
        </div>
      </section>

      {/* Prerequisite Notice */}
      <section className="px-4 sm:px-6 lg:px-8 pb-8" data-testid="intermediate-prerequisite">
        <div className="mx-auto max-w-3xl">
          <div className="rounded-2xl bg-amber-500/[0.05] border border-amber-500/20 p-5 flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
              <span className="text-amber-500 font-bold text-lg">!</span>
            </div>
            <div>
              <p className="text-sm font-bold text-white mb-1">Prerequisite: Beginner Courses</p>
              <p className="text-xs text-slate-400 leading-relaxed">
                The 9-Step Pro System assumes you&apos;ve completed the <a href="/courses" className="text-amber-500 underline hover:text-amber-400">Forex Made Simple</a> beginner course — the foundation Hettie teaches every new trader.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 border-t border-white/[0.04]" data-testid="pricing-section">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-bold text-white mb-2 text-center">Intermediate Courses</h2>
          <p className="text-sm text-white/40 mb-10 text-center">One-time payment. Lifetime access. 30-day money-back guarantee.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {PRICING.map(plan => {
              const inCart = items.some(i => i.slug === plan.slug);
              const isScalp = plan.accent === 'scalp';
              return (
                <div key={plan.slug} className={`rounded-xl border p-6 flex flex-col ${plan.popular ? 'border-amber-400/30 bg-amber-400/[0.03] ring-1 ring-amber-400/10' : isScalp ? 'border-sky-400/30 bg-sky-400/[0.03] ring-1 ring-sky-400/10' : 'border-white/[0.06] bg-white/[0.02]'}`} data-testid={`pricing-${plan.slug}`}>
                  <p className={`text-[10px] font-semibold uppercase tracking-wider mb-3 ${plan.popular ? 'text-amber-400' : isScalp ? 'text-sky-400' : 'text-white/50'}`}>{plan.badge}</p>
                  <h3 className="text-lg font-bold text-white">{plan.name}</h3>
                  <p className="text-xs text-white/40 mt-0.5">{plan.subtitle}</p>
                  <div className="mt-4 flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-white">${plan.price}</span>
                    {plan.originalPrice && <span className="text-sm text-white/30 line-through">${plan.originalPrice}</span>}
                  </div>
                  <p className="text-[10px] text-white/30 mt-1">One-time payment</p>
                  <button
                    onClick={() => addItem({ slug: plan.slug, name: plan.name, price: plan.price, image_url: '', platform: 'MT5' })}
                    disabled={inCart}
                    className={`mt-5 w-full py-2.5 rounded-lg text-sm font-semibold transition-colors ${inCart ? 'bg-white/5 text-white/30' : isScalp ? 'bg-sky-400 text-black hover:bg-sky-300' : 'bg-amber-400 text-black hover:bg-amber-300'}`}
                    data-testid={`pricing-add-${plan.slug}`}
                  >
                    {inCart ? 'In Cart' : 'Get Started'}
                  </button>
                  <ul className="mt-5 space-y-2 flex-1">
                    {plan.features.map((f, i) => (
                      <li key={i} className={`flex items-start gap-2 text-xs ${f.included ? 'text-white/50' : 'text-white/20'}`}>
                        {f.included ? <Check size={13} className={`${isScalp ? 'text-sky-400' : 'text-amber-400'} mt-0.5 shrink-0`} /> : <XIcon size={13} className="text-white/15 mt-0.5 shrink-0" />}
                        <span className={f.included ? '' : 'line-through'}>{f.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 border-t border-white/[0.04] text-center" data-testid="intermediate-cta">
        <div className="mx-auto max-w-2xl">
          <h2 className="text-3xl font-bold text-white">Going further?</h2>
          <p className="mt-3 text-sm text-white/40">Once you&apos;ve mastered day trading and scalping, level up with longer-horizon swing &amp; position systems — or grab the full Pro Trader Bundle.</p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/advanced" className="px-6 py-3 rounded-lg bg-amber-400 text-black font-semibold text-sm hover:bg-amber-300 transition-colors" data-testid="intermediate-cta-advanced">
              Explore Advanced Courses
            </Link>
            <Link to="/courses" className="px-6 py-3 rounded-lg border border-white/10 text-white/60 text-sm hover:border-white/20 hover:text-white transition-colors" data-testid="intermediate-cta-beginner">
              Back to Beginners
            </Link>
          </div>
          <p className="mt-4 text-[10px] text-white/20">Secure payment via Stripe - Instant download after purchase</p>
        </div>
      </section>

      {/* Trust */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 border-t border-white/[0.04]" data-testid="intermediate-trust">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-2xl font-bold text-white mb-4">Why Traders Trust Hettie Nel</h2>
          <ul className="space-y-3 text-sm text-white/50">
            <li className="flex items-start gap-2"><Star size={14} className="text-amber-400 mt-0.5 shrink-0" /> 20+ years of active forex trading and one-on-one training</li>
            <li className="flex items-start gap-2"><Star size={14} className="text-amber-400 mt-0.5 shrink-0" /> 4.9/5 rating on Forex Peace Army (71+ verified reviews)</li>
            <li className="flex items-start gap-2"><Star size={14} className="text-amber-400 mt-0.5 shrink-0" /> 900+ students trained through personal sessions</li>
          </ul>
        </div>
      </section>
    </div>
  );
}
